import { format } from "date-fns"
import { groupBy } from "es-toolkit/array"
import { TFunction } from "i18next"
import { getDateFnsLocale } from "@/lib/date-fns-locale"
import { joinStrings } from "@/lib/joinString"
import { chAuditQuery$data } from "@/modules/patients/history/services/ch/__generated__/chAuditQuery.graphql"
import { deAuditQuery$data } from "@/modules/patients/history/services/de/__generated__/deAuditQuery.graphql"
import { Payment } from "@/types/__generated__/union-types"

export interface TimelineEntry {
  time: string
  action: string
}

export interface UsernameEntry {
  username: string
  content: TimelineEntry[]
}

export interface DateEntry {
  date: string
  content: UsernameEntry[]
}

export interface YearEntry {
  year: string
  content: DateEntry[]
}

type AuditEventTransformed = {
  userName: string
  content: {
    time: Date
    action: string
  }
}

// The audit payload carries the C# enum member name; the locale keys use the GraphQL spelling
const BETTER_DOC_REJECTION_REASON_KEY = {
  PatientRefusesToUseBetterDoc: "PATIENT_REFUSES_TO_USE_BETTER_DOC",
  BetterDocChoseCareProviderBeforehand: "BETTER_DOC_CHOSE_CARE_PROVIDER_BEFOREHAND",
} as const

type CoveragesActionType = Array<{
  Coverages: Array<{ OrganisationName?: string; ProductName?: string; Payment?: Payment }>
}>

const handleFlatCoveragesAuditEvent = (actions: CoveragesActionType, t: TFunction) => {
  return actions
    .flatMap((insuree) => insuree.Coverages)
    .map((cov) => {
      // for previous versions where OrganisationName, ProductName can be null
      if (!cov.OrganisationName || !cov.ProductName) return ""

      const paymentInfo = cov.Payment
        ? t(`patient.serviceIndicator.payment.${cov.Payment}.text`)
        : t("patient.healthInformation.none")
      return joinStrings([`${cov.OrganisationName} (${cov.ProductName})`, paymentInfo], " - ")
    })
    .filter((str) => !!str)
}

const transformCoveragesAuditEvent = (jsonPayload: string | null, t: TFunction): string => {
  if (!jsonPayload) return ""

  const payload: { Before: CoveragesActionType; After: CoveragesActionType } = JSON.parse(jsonPayload)

  const beforeCoverages = handleFlatCoveragesAuditEvent(payload.Before, t)
  const afterCoverages = handleFlatCoveragesAuditEvent(payload.After, t)

  if (beforeCoverages.length === 0 && afterCoverages.length === 0) return ""

  let action = `:\n </br> ${beforeCoverages.length === 0 ? t("patient.healthInformation.none") : joinStrings(beforeCoverages, ", ")}\n <br/>`
  return (action += `=> \n <br/> ${joinStrings(afterCoverages, ", ")}`)
}

const transformAuditEventsToTimeline = (
  auditEvents: chAuditQuery$data["auditEvents"] & deAuditQuery$data["auditEvents"],
  t: TFunction,
  language: string,
): YearEntry[] => {
  if (!auditEvents?.edges) return []

  const auditEventTransformed = auditEvents.edges
    .map<AuditEventTransformed>((edge) => {
      const { node } = edge

      switch (node.action) {
        case "InboundCallWizardFinalized": {
          const payload: {
            InboundCallStartedAt: string
            CallerPhoneNumber: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(payload.InboundCallStartedAt),
              action: t(`patient.history.actions.${node.action}`, { phoneNumber: payload.CallerPhoneNumber }),
            },
          }
        }

        case "StartOutboundCall":
        case "CallPatient": {
          const payload: { PhoneNumber: string; IsRecorded?: boolean } = JSON.parse(node.jsonPayload ?? "{}")

          const { PhoneNumber: phoneNumber, IsRecorded } = payload

          // rows written before the recording state was tracked carry no IsRecorded key and keep the neutral wording
          const action =
            typeof IsRecorded === "boolean"
              ? IsRecorded
                ? t("patient.history.actions.StartOutboundCallRecordedToNumber", { phoneNumber })
                : t("patient.history.actions.StartOutboundCallUnrecordedToNumber", { phoneNumber })
              : t("patient.history.actions.StartOutboundCallToNumber", { phoneNumber })

          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action,
            },
          }
        }

        case "InfoPackageSentFirstContact":
        case "InfoPackageSentLateRegistration":
        case "InfoPackageSentSelfReferral": {
          const payload: {
            InfoPackageLink: string
            Channel: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, {
                link: `<a href="${payload.InfoPackageLink}">${payload.InfoPackageLink}</a>`,
                channel: payload.Channel,
              }),
            },
          }
        }

        case "SentNotificationToPatient": {
          const payload: {
            NotificationType: string
            Channel: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t("patient.history.actions.SentNotificationToPatient", {
                notificationType: payload.NotificationType,
                channel: payload.Channel,
              }),
            },
          }
        }

        case "SentVideoCallLinkToPatient": {
          const payload: {
            PhoneNumber?: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t("patient.history.actions.SentVideoCallLinkToPatient", { phoneNumber: payload.PhoneNumber }),
            },
          }
        }

        case "AllergyInformation": {
          const payload: {
            Actions: Record<string, Array<{ DiagnosisCode: string; Comment?: string }>>
          } = JSON.parse(node.jsonPayload ?? "{}")

          let action = ""
          for (const key in payload.Actions ?? {}) {
            if (!payload.Actions[key]) continue
            action += `${t(`patient.history.actions.${key}`)}:\n <br/> ${payload.Actions[key]
              .map(({ DiagnosisCode, Comment }) => joinStrings([DiagnosisCode, Comment], " - "))
              .join("\n <br/>")}\n <br/>`
          }

          return { userName: node.userName, content: { time: new Date(node.eventTime), action } }
        }

        case "SavedInsureesCoverages": {
          const action = transformCoveragesAuditEvent(node.jsonPayload, t)
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`) + action,
            },
          }
        }

        case "SavePreventionProgramEngagement": {
          const payload: {
            PreventionProgramName: string
            Status: string
            DismissReason: string | null
          } = JSON.parse(node.jsonPayload ?? "{}")

          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, {
                programName: payload.PreventionProgramName,
                status: t(`patient.prevention.status.${payload.Status}`),
              }),
            },
          }
        }

        case "ResendReferralLetter": {
          const payload: {
            CareProvider: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, { careProvider: payload.CareProvider }),
            },
          }
        }

        case "RejectBetterDoc": {
          const payload: {
            BetterDocRejectionReason: keyof typeof BETTER_DOC_REJECTION_REASON_KEY
          } = JSON.parse(node.jsonPayload ?? "{}")
          const reasonKey = BETTER_DOC_REJECTION_REASON_KEY[payload.BetterDocRejectionReason]
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, {
                reason: t(`episodeOfCare.betterDoc.rejectionReasons.${reasonKey}`),
              }),
            },
          }
        }

        case "UpdateProgressNoteCareProvider": {
          const payload: {
            PreviousCareProvider: string | null
            NewCareProvider: string
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, {
                previous: payload.PreviousCareProvider,
                new: payload.NewCareProvider,
              }),
            },
          }
        }

        case "ConfirmDuplicatePatients": {
          const payload: {
            DuplicatedPatientIds: number[]
          } = JSON.parse(node.jsonPayload ?? "{}")
          const duplicates = `[${(payload.DuplicatedPatientIds ?? []).join(", ")}]`
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, { duplicates }),
            },
          }
        }

        case "ConfirmDuplicatedPatient": {
          const payload: {
            PrimaryPatientId: number
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, { id: payload.PrimaryPatientId }),
            },
          }
        }

        case "UndoDuplicatedPatient": {
          const payload: {
            PrimaryPatientId: number
          } = JSON.parse(node.jsonPayload ?? "{}")
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`, { id: payload.PrimaryPatientId }),
            },
          }
        }

        default:
          return {
            userName: node.userName,
            content: {
              time: new Date(node.eventTime),
              action: t(`patient.history.actions.${node.action}`),
            },
          }
      }
    })
    .toSorted((e1, e2) => e2.content.time.getTime() - e1.content.time.getTime())

  const groupedAuditEvents = groupBy(auditEventTransformed, (event) => new Date(event.content.time).getFullYear())

  const groupedByYear = Object.keys(groupedAuditEvents)
    .map((k) => ({ year: k, group: groupedAuditEvents[+k] }))
    .sort((a, b) => +b.year - +a.year)

  const result: YearEntry[] = groupedByYear.map((item) => {
    const yearEvents = item.group
    const groupedByDate = groupBy(yearEvents, (event) =>
      format(event.content.time, "dd. MMM", { locale: getDateFnsLocale(language) }),
    )

    const dateEntries: DateEntry[] = Object.keys(groupedByDate).map((date) => {
      const dateEvents = groupedByDate[date]
      const result = dateEvents.reduce<UsernameEntry[]>((dateContent, item) => {
        const time = format(item.content.time, "HH:mm")
        const lastGroup = dateContent.length > 0 ? dateContent[dateContent.length - 1] : undefined

        if (lastGroup && lastGroup.username === item.userName) {
          lastGroup.content.push({ time, action: item.content.action })
        } else {
          dateContent.push({
            username: item.userName,
            content: [{ time, action: item.content.action }],
          })
        }

        return dateContent
      }, [])

      return { date, content: result }
    })

    return { year: item.year, content: dateEntries }
  })

  return result
}

export { transformAuditEventsToTimeline }
